/**
 * Warm a lazy route's chunk BEFORE it is asked for.
 *
 * Every screen in `App.js` that matters here is `React.lazy`, so the first tap
 * on it pays for a JavaScript download before React can render anything at
 * all. That is the whole of the wait left on "Enter Scores" once the data was
 * dealt with: the scoring card is a ~40KB chunk, and "Rounds" — which feels
 * instant beside it — is not lazy at all and ships in the main bundle.
 *
 * These are the SAME dynamic imports `App.js` makes, so webpack gives them the
 * same chunk: warming one here means `React.lazy` resolves from memory when
 * the user actually navigates, with no Suspense fallback at all.
 *
 * Nothing here is required for correctness. A failed warm is forgotten and the
 * route loads the ordinary way, which is why every call swallows its error.
 */

// Path → the import that fetches the screen behind it. Only the routes a
// scorer reaches from the bottom bar mid-round; warming everything would just
// move the download cost somewhere else on the same thin connection.
const LOADERS = [
  [/^\/live-scoring\//, () => import('./LiveScoring')],
  [/^\/team-scoring\//, () => import('./TeamLiveScoring')],
  [/^\/tournaments\/[^/]+\/scorecard-input/, () => import('./ScorecardInput')],
  [/^\/leaderboard/, () => import('./Leaderboard')],
  [/^\/play/, () => import('./PlayHub')],
];

const warmed = new Set();

/**
 * Fetch the chunk behind `path`, once per session. Safe to call on every
 * pointerdown: the second call is a Set lookup.
 */
export function preloadRoute(path) {
  if (!path) return;
  const entry = LOADERS.find(([pattern]) => pattern.test(path));
  if (!entry || warmed.has(entry[0])) return;
  warmed.add(entry[0]);
  try {
    Promise.resolve(entry[1]()).catch(() => warmed.delete(entry[0]));
  } catch {
    warmed.delete(entry[0]);
  }
}

/**
 * The same, but waiting for a quiet moment — for warming a route the user has
 * not reached for yet, so it never competes with the screen they are on.
 *
 * `requestIdleCallback` is missing on older WebKit, where a plain timeout is
 * the honest substitute: late is fine, since this is all speculative anyway.
 */
export function preloadRouteWhenIdle(path, delay = 1200) {
  if (!path) return;
  const go = () => preloadRoute(path);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(go, { timeout: 4000 });
  } else {
    setTimeout(go, delay);
  }
}
